import { DocumentV2, photoTypeRegex, videoTypeRegex } from '@eigtech/documents-types'
import { baseScreenshotLabels } from '@eigtech/meetings-types'
import {
  Button,
  Center,
  chakra,
  ComposedAlert,
  ComposedModalWrapperProps,
  Image,
  Tooltip,
  useToast,
  withModalErrorBoundary,
  WithModalErrorBoundaryProps,
} from '@eigtech/ui-shared-dave'
import {
  UpdateDocumentRequest,
  useGetDocumentUrl,
  useUpdateDocument,
} from '@eigtech/ui-shared-documents'
import { createForm, DefaultValues, FormModal } from '@eigtech/ui-shared-forms'
import { startCase } from 'lodash-es'
import { z } from 'zod'

export type UpdateDocumentLabelModalProps = ComposedModalWrapperProps & {
  document: DocumentV2
}

export const UpdateDocumentLabelModal = withModalErrorBoundary(
  function UpdateDocumentLabelModal({ document, ...props }: UpdateDocumentLabelModalProps) {
    const { data: url } = useGetDocumentUrl({
      entityId: document.entityId,
      documentId: document.id,
    })

    const defaultValues: DefaultValues<UpdateDocumentForm> = {}

    if (document.metaData?.label) {
      defaultValues.label = {
        label: startCase(document.metaData.label.toLowerCase()),
        value: document.metaData.label,
      }
    }

    const form = useForm({ defaultValues })

    const toast = useToast()

    const { mutateAsync: updateDocument, isError } = useUpdateDocument()

    async function handleSubmit({ label }: UpdateDocumentForm) {
      const payload: UpdateDocumentRequest = {
        entityId: document.entityId,
        documentId: document.id,
        description: document.description,
        notes: document.notes,
      }

      if (label) {
        payload.metaData = {
          ...document.metaData,
          label: label.value.toUpperCase(),
        }

        const originalLabel = document.metaData?.originalLabel ?? document.metaData?.label
        if (originalLabel) {
          payload.metaData.originalLabel = originalLabel
        }
      }

      await updateDocument(payload)

      toast({
        status: 'success',
        title: 'Successfully updated document information!',
      })

      props.onClose()
    }

    return (
      <FormModal {...props} {...baseProps} form={form} onSubmit={handleSubmit}>
        {!!url && (
          <Tooltip label={document.filename}>
            <Center>
              {document.filename.match(videoTypeRegex) ? (
                <chakra.video src={url} w="full" />
              ) : document.filename?.match(photoTypeRegex) ? (
                <chakra.a href={url} target={document.filename}>
                  <Image maxH="30vh" src={url} />
                </chakra.a>
              ) : (
                <Button as="a" href={url} target={document.filename} variant="outline">
                  Open Document Preview
                </Button>
              )}
            </Center>
          </Tooltip>
        )}

        <ReactCreatableSelectField label="Label" name="label" options={baseScreenshotLabels} />

        {isError && (
          <ComposedAlert
            alert={{
              title: 'Something went wrong',
              description:
                "We could not update this file's information. Please try again. If the issue persists, please contact EIG Customer Service.",
            }}
            status="error"
          />
        )}
      </FormModal>
    )
  },
  () => baseProps
)

const baseProps: WithModalErrorBoundaryProps = {
  title: 'Update Document Label',
}

export const UpdateDocumentLabelSchema = z.object({
  label: z.object({ label: z.string(), value: z.string().min(1) }),
})

type UpdateDocumentForm = z.infer<typeof UpdateDocumentLabelSchema>

const { useForm, ReactCreatableSelectField } = createForm(
  UpdateDocumentLabelSchema,
  'updateDocumentLabelForm'
)
